import { z } from "zod/v4";

export const SOUND_PRESET_IDS = ["chime", "bell", "ding", "electronic", "nature", "silent"] as const;
export type SoundPresetId = (typeof SOUND_PRESET_IDS)[number];

const soundConfigSchema = z.object({
  preset: z.enum(SOUND_PRESET_IDS),
  volume: z.number().int().min(0).max(100),
});

const pomodoroSettingsSchema = z.object({
  workDuration: z.number().int().min(1).max(120),
  shortBreakDuration: z.number().int().min(1).max(30),
  longBreakDuration: z.number().int().min(1).max(60),
  sessionsBeforeLongBreak: z.number().int().min(1).max(12),
  autoStartNext: z.boolean(),
  autoStartOnSkip: z.boolean(),
  workEndSound: soundConfigSchema,
  breakEndSound: soundConfigSchema,
});

const twentySettingsSchema = z.object({
  screenDuration: z.number().int().min(1).max(60),
  breakDuration: z.number().int().min(5).max(120),
  autoStart: z.boolean(),
  autoStartOnSkip: z.boolean(),
  transitionSound: soundConfigSchema,
});

const stopwatchSettingsSchema = z.object({
  showMilliseconds: z.boolean(),
  lapSound: soundConfigSchema,
});

export const timerSettingsSchema = z.object({
  pomodoro: pomodoroSettingsSchema,
  twenty: twentySettingsSchema,
  stopwatch: stopwatchSettingsSchema,
});

export type SoundConfig = z.infer<typeof soundConfigSchema>;
export type PomodoroSettings = z.infer<typeof pomodoroSettingsSchema>;
export type TwentySettings = z.infer<typeof twentySettingsSchema>;
export type StopwatchSettings = z.infer<typeof stopwatchSettingsSchema>;
export type TimerSettings = z.infer<typeof timerSettingsSchema>;

export const POMODORO_MODES = ["work", "break", "longBreak"] as const;
export type PomodoroMode = (typeof POMODORO_MODES)[number];

export const TWENTY_MODES = ["screen", "break"] as const;
export type TwentyMode = (typeof TWENTY_MODES)[number];

export const DEFAULT_TIMER_SETTINGS: TimerSettings = {
  pomodoro: {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    sessionsBeforeLongBreak: 4,
    autoStartNext: true,
    autoStartOnSkip: true,
    workEndSound: { preset: "chime", volume: 50 },
    breakEndSound: { preset: "electronic", volume: 30 },
  },
  twenty: {
    screenDuration: 20,
    breakDuration: 20,
    autoStart: false,
    autoStartOnSkip: true,
    transitionSound: { preset: "ding", volume: 30 },
  },
  stopwatch: {
    showMilliseconds: false,
    lapSound: { preset: "silent", volume: 0 },
  },
};
